import { waitFor } from '@/lib/helpers/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { AUTHENTICATION, broadcast } from '../broadcastChannels/channels';
import { SIGN_OUT } from '../redux/actions/types';
import { clearAuthCSRFTokenCache } from '../services/authCSRFToken';
import { getUser } from '../services/user';
import { User } from '../user/profile/profileUtils';

export type AuthLevel = 'NONE' | 'PARTIAL' | 'FULL';

export type AuthSessionUser = {
  id: string;
  givenName: string;
  profileCompletion: number;
};

export type AuthenticatedSession = {
  isAuthenticated: true;
  authLevel: Exclude<AuthLevel, 'NONE'>;
  user: AuthSessionUser;
  roles: string[];
};

export type UnauthenticatedSession = {
  isAuthenticated: false;
  authLevel: 'NONE';
  user: null;
  roles: [];
};

export type AuthSession = AuthenticatedSession | UnauthenticatedSession;

type LoginCredentials = {
  email: string;
  password: string;
  rememberMe?: boolean;
};

type RegisterPayload = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  googleId?: string;
  facebookId?: string;
  profile?: Record<string, unknown>;
};

export type RegisterValidationDetails = {
  detail: string;
  email?: string;
  password?: string;
  general?: string;
};

type ApiProblemValidationError = {
  property: string;
  error: string[];
};

type ApiProblemResponse = {
  error: true;
  value: {
    title: string;
    detail: string;
    validationErrors?: ApiProblemValidationError[];
  };
};

export class RegisterValidationError extends Error {
  readonly details: RegisterValidationDetails;

  constructor(details: RegisterValidationDetails) {
    super(details.detail);
    this.name = 'RegisterValidationError';
    this.details = details;
  }
}

export const AUTH_SESSION_QUERY_KEY = ['authSession'] as const;
export const CURRENT_USER_QUERY_KEY = ['currentUser'] as const;
const AUTHENTICATED_QUERY_KEYS = [['tracked-opportunities'], ['trackers'], ['savedSearches']] as const;
const LINK_LOGIN_SESSION_MAX_ATTEMPTS = 3;
const LINK_LOGIN_SESSION_RETRY_DELAY_MS = 250;

const LOGGED_OUT_SESSION: UnauthenticatedSession = {
  isAuthenticated: false,
  authLevel: 'NONE',
  user: null,
  roles: []
};

export async function fetchAuthSession(): Promise<AuthSession> {
  try {
    const response = await axios.get<AuthSession>('/api/auth/session');
    return response.data;
  } catch {
    return LOGGED_OUT_SESSION;
  }
}

export async function login(credentials: LoginCredentials): Promise<AuthSession> {
  const response = await axios.post<AuthSession>(`/api/login?remember_me=${credentials.rememberMe ?? false}`, {
    email: credentials.email,
    password: credentials.password
  });
  return response.data;
}

export async function logout(): Promise<AuthSession> {
  const response = await axios.get<AuthSession>(`/api/logout?t=${Date.now()}`, {
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache'
    }
  });
  return response.data;
}

type LinkLoginRequest = {
  hash: string;
  path: string | null;
};

type LinkLoginApiResponse = {
  path?: string | null;
  user?: AuthSessionUser | null;
  isAuthenticated?: boolean;
  authLevel?: AuthLevel;
  roles?: string[];
};

export type LinkLoginResult = {
  session: AuthSession;
  redirectTo: string | null;
};

function getSessionFromLinkLoginResponse(data: LinkLoginApiResponse): AuthSession | null {
  if (data.isAuthenticated === true && data.user && data.authLevel && data.authLevel !== 'NONE') {
    return {
      isAuthenticated: true,
      authLevel: data.authLevel,
      user: data.user,
      roles: data.roles ?? []
    };
  }

  if (data.isAuthenticated === false) {
    return LOGGED_OUT_SESSION;
  }

  return null;
}

export async function linkLogin(hash: string, path: string | null): Promise<LinkLoginApiResponse> {
  const response = await axios.post<LinkLoginApiResponse>(`/api/linklogin/${hash}`, {
    path
  });

  if (response.status !== 200) {
    throw new Error('Link login failed');
  }

  return response.data;
}

async function fetchAuthSessionAfterLinkLogin(hasUserFromResponse: boolean): Promise<AuthSession> {
  let session = await fetchAuthSession();
  if (session.isAuthenticated || !hasUserFromResponse) {
    return session;
  }

  for (let attempt = 1; attempt < LINK_LOGIN_SESSION_MAX_ATTEMPTS; attempt++) {
    await waitFor(LINK_LOGIN_SESSION_RETRY_DELAY_MS * attempt);
    session = await fetchAuthSession();
    if (session.isAuthenticated) {
      return session;
    }
  }

  return session;
}

export async function register(payload: RegisterPayload): Promise<AuthSession> {
  try {
    const response = await axios.post<AuthSession>('/api/register', payload);
    return response.data;
  } catch (error) {
    if (error instanceof Error && axios.isAxiosError<ApiProblemResponse>(error) && error.response?.status === 400) {
      const apiProblem = error.response.data;
      const details: RegisterValidationDetails = {
        detail: apiProblem.value.detail || apiProblem.value.title || error.message
      };

      for (const validationError of apiProblem.value.validationErrors ?? []) {
        const message = validationError.error[0];
        if (!message) {
          continue;
        }

        if (validationError.property === 'email') {
          details.email = message;
        }
        if (validationError.property === 'password') {
          details.password = message;
        }
        if (validationError.property === 'general') {
          details.general = message;
        }
      }

      throw new RegisterValidationError(details);
    }

    throw error;
  }
}

export function useAuthSession() {
  return useQuery<AuthSession>({
    queryKey: AUTH_SESSION_QUERY_KEY,
    queryFn: fetchAuthSession,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: false
  });
}

export async function fetchCurrentUser(): Promise<User> {
  const response = await getUser();

  if (!response.success) {
    throw new Error(`Unable to retrieve current user profile. Status: ${response.status}`);
  }

  return response.user;
}

export function useCurrentUser(userId: string | undefined, enabled: boolean = true) {
  return useQuery<User>({
    queryKey: [...CURRENT_USER_QUERY_KEY, userId],
    queryFn: fetchCurrentUser,
    enabled: enabled && Boolean(userId),
    refetchOnWindowFocus: false,
    retry: false
  });
}

export function useLoginMutation() {
  const queryClient = useQueryClient();

  return useMutation<AuthSession, Error, LoginCredentials>({
    mutationFn: login,
    onSuccess: (session) => {
      clearAuthCSRFTokenCache();
      queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, session);
      broadcast(AUTHENTICATION, { authChanged: true });
    }
  });
}

export function useLogoutMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation<AuthSession, Error, void>({
    mutationFn: logout,
    onSuccess: async (session) => {
      clearAuthCSRFTokenCache();
      queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, session);
      await Promise.all(
        AUTHENTICATED_QUERY_KEYS.map((queryKey) => queryClient.cancelQueries({ queryKey, exact: false }))
      );
      AUTHENTICATED_QUERY_KEYS.forEach((queryKey) => {
        queryClient.removeQueries({ queryKey, exact: false });
      });
      queryClient.invalidateQueries();
      sessionStorage.clear();
      dispatch({ type: SIGN_OUT });
      broadcast(AUTHENTICATION, { authChanged: true });
    }
  });
}

export function useRegisterMutation() {
  const queryClient = useQueryClient();

  return useMutation<AuthSession, Error, RegisterPayload>({
    mutationFn: register,
    onSuccess: (session) => {
      clearAuthCSRFTokenCache();
      queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, session);
      broadcast(AUTHENTICATION, { authChanged: true });
    }
  });
}

export function useLinkLoginMutation() {
  const queryClient = useQueryClient();

  return useMutation<LinkLoginResult, Error, LinkLoginRequest>({
    mutationFn: async ({ hash, path }) => {
      const data = await linkLogin(hash, path);
      await queryClient.cancelQueries({ queryKey: AUTH_SESSION_QUERY_KEY });

      const sessionFromResponse = getSessionFromLinkLoginResponse(data);
      const session = sessionFromResponse ?? (await fetchAuthSessionAfterLinkLogin(Boolean(data.user)));

      return {
        session,
        redirectTo: data.path ?? null
      };
    },
    onSuccess: async ({ session }) => {
      clearAuthCSRFTokenCache();
      queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, session);
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] !== AUTH_SESSION_QUERY_KEY[0]
      });
      broadcast(AUTHENTICATION, { authChanged: true });
    }
  });
}
